import { useState } from 'react';
import {
  UMButton, UMDrawToolbar, UMFormStyles, UMIcon, UMLayerGroup, UMLegendItem, UMMessage, UMR, UMSidePanel, UMStack, UM_GEOMETRY_TOOLS,
  type UMRailItem, type UMRailItemId,
} from '../../urbanmap/_components';
import { HEAT_STEPS, type PlanLayerVisibility } from './mapLayers';
import { PLAN } from './tokens';
import type { PlanProject } from './types';
import { Pill, PlanLogo, fmt } from './ui';

// ─── Rail ──────────────────────────────────────────────────────────────────

export const PLAN_RAIL_ITEMS: UMRailItem[] = [
  { id: 'layers', icon: 'layers', label: 'Couches', group: 'tools' },
  { id: 'edit', icon: 'edit_location_alt', label: 'Édition', group: 'tools' },
  { id: 'measures', icon: 'straighten', label: 'Mesures', group: 'tools' },
  { id: 'filters', icon: 'filter_list', label: 'Filtres', group: 'data' },
  { id: 'selection', icon: 'folder_open', label: 'Sélection', group: 'data' },
  { id: 'export', icon: 'download', label: 'Export', group: 'io' },
  { id: 'import', icon: 'upload', label: 'Import', group: 'io' },
];

/** Same metrics as UMRail (80px, 59px items) with the Plan logo and a visible active state. */
export function PlanRail({ activeId, onSelect }: { activeId: UMRailItemId | null; onSelect: (id: UMRailItemId) => void }) {
  return (
    <nav
      aria-label="Outils carte du projet"
      style={{ width: UMR.size.railWidth, minWidth: UMR.size.railWidth, height: '100%', borderRight: `1px solid ${UMR.border}`, background: UMR.white, display: 'flex', flexDirection: 'column', zIndex: 30, fontFamily: UMR.font }}
    >
      <div style={{ padding: '16px 0', display: 'flex', justifyContent: 'center' }}>
        <PlanLogo size={UMR.size.logo} />
      </div>
      {PLAN_RAIL_ITEMS.map((item, i) => {
        const active = activeId === item.id;
        return (
          <div key={item.id}>
            {i > 0 && PLAN_RAIL_ITEMS[i - 1].group !== item.group && <div style={{ margin: '12px 0', borderTop: `1px solid ${UMR.borderSubtle}` }} />}
            <button
              type="button"
              aria-pressed={active}
              onClick={() => onSelect(item.id)}
              style={{
                width: '100%', height: UMR.size.railItemHeight, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '8px 0', border: 'none',
                borderLeft: `3px solid ${active ? PLAN.accent : 'transparent'}`, background: active ? PLAN.accentBg : 'transparent', color: active ? PLAN.accentStrong : UMR.text,
                cursor: 'pointer', fontFamily: UMR.font,
              }}
            >
              <UMIcon name={item.icon} size={UMR.size.railIcon} color={active ? PLAN.accentStrong : UMR.text} />
              <span style={{ fontSize: UMR.size.railLabel, lineHeight: '16px' }}>{item.label}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}

// ─── Layers ────────────────────────────────────────────────────────────────

function GroupHeader({ icon, title, badge, tone }: { icon: string; title: string; badge: string; tone: 'accent' | 'neutral' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0 8px', borderBottom: `1px solid ${UMR.borderSubtle}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 14, color: UMR.textStrong }}>
        <UMIcon name={icon} size={18} color={tone === 'accent' ? PLAN.accent : UMR.blue} />
        {title}
      </div>
      <div><Pill tone={tone} icon={tone === 'accent' ? 'edit' : 'visibility'}>{badge}</Pill></div>
    </div>
  );
}

export function ProjectLayersPanel({ project, visibility, onChange, modifiedCount, insideCount, onClose, scenarioName }: {
  project: PlanProject; visibility: PlanLayerVisibility; onChange: (v: PlanLayerVisibility) => void; modifiedCount: number; insideCount: number; onClose: () => void; scenarioName?: string;
}) {
  const set = (k: keyof PlanLayerVisibility) => (value: boolean) => onChange({ ...visibility, [k]: value });
  return (
    <UMSidePanel title="Couches" onClose={onClose}>
      <UMStack gap={12}>
        <GroupHeader icon="content_copy" title="Données du projet" badge={`Copie figée du ${project.snapshotDate} · modifiable`} tone="accent" />
        <UMLayerGroup label={`Bâtiments du périmètre (${fmt(insideCount)})`} checked={visibility.buildings} onCheckedChange={set('buildings')} defaultOpen>
          {HEAT_STEPS.map((s) => <UMLegendItem key={s.label} label={s.label} color={s.color} />)}
          <UMLegendItem label={`Modifiés dans le projet (${modifiedCount})`} color={PLAN.map.modified} kind="line" disabled={!visibility.modified} onClick={() => set('modified')(!visibility.modified)} />
        </UMLayerGroup>
        <UMLayerGroup label="Voirie (copie modifiable)" checked={visibility.roads} onCheckedChange={set('roads')}>
          <UMLegendItem label="Routes du projet" color={PLAN.map.road} kind="line" />
        </UMLayerGroup>
        <UMLayerGroup label="Réseaux de chaleur (copie)" checked={visibility.pipes} onCheckedChange={set('pipes')}>
          <UMLegendItem label="Réseaux ENGIE (Arcueil-Gentilly, CPCU)" color={UMR.map.pipe} kind="line" />
          <UMLegendItem label="Autres réseaux (Ivry, SEMHACH)" color={PLAN.map.pipeThirdParty} kind="line" />
        </UMLayerGroup>
        {scenarioName && (
          <div style={{ padding: 8, background: 'var(--nj-core-color-purple-100, #f1eafb)', fontSize: 12 }}>
            <UMLegendItem label={`Scénario affiché : ${scenarioName}`} color={PLAN.map.scenario} kind="line" />
          </div>
        )}

        <div style={{ height: 8 }} />
        <GroupHeader icon="sync" title="Couches de référence UrbanMap" badge="En direct · lecture seule · hors périmètre" tone="neutral" />
        <UMLayerGroup label="Zones de développement prioritaire" checked={visibility.zone} onCheckedChange={set('zone')}>
          <UMLegendItem label="Réseau Arcueil-Gentilly (non limité au périmètre)" color={PLAN.map.zone} />
        </UMLayerGroup>
        <UMLayerGroup label="Points de livraison SNCU" checked={visibility.delivery} onCheckedChange={set('delivery')}>
          <UMLegendItem label="Consommation mesurée (taille ∝ MWh)" color={PLAN.map.delivery} />
        </UMLayerGroup>
        <UMLayerGroup label="Bâti UrbanMap hors périmètre" checked={visibility.outside} onCheckedChange={set('outside')}>
          <UMLegendItem label="Non copié dans le projet" color={PLAN.map.outside} />
        </UMLayerGroup>
        <UMLayerGroup label={`Écarts UrbanMap depuis la copie (${project.umDrift.length})`} checked={visibility.drift} onCheckedChange={set('drift')}>
          <UMLegendItem label="Bâtiment modifié dans UrbanMap" color={UMR.warning} kind="line" />
        </UMLayerGroup>
        <div style={{ fontSize: 12, color: UMR.textSecondary }}>
          Les autres couches UrbanMap (cadastre, IRIS, potentiels EnR&R…) restent disponibles en lecture seule.
        </div>
      </UMStack>
    </UMSidePanel>
  );
}

// ─── Edit ──────────────────────────────────────────────────────────────────

export function EditPanel({ onClose, overrides, original, onRevert, onSelect, hasBuilding }: {
  onClose: () => void; overrides: Record<string, number>; original: Record<string, number>; onRevert: (fid: string) => void; onSelect: (fid: string) => void; hasBuilding: boolean;
}) {
  const [tool, setTool] = useState<string | null>(null);
  const [target, setTarget] = useState('Bâtiments');
  const entries = Object.entries(overrides);
  return (
    <UMSidePanel title="Édition du projet" onClose={onClose}>
      <UMFormStyles />
      <UMStack gap={12}>
        <UMMessage>Les modifications restent dans ce projet. <strong>UrbanMap n’est pas modifié.</strong></UMMessage>
        <div style={{ display: 'flex', gap: 4 }}>
          {['Bâtiments', 'Voirie', 'Réseau'].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTarget(t)}
              style={{
                flex: 1, height: 30, fontFamily: UMR.font, fontSize: 13, cursor: 'pointer',
                border: `1px solid ${t === target ? PLAN.accent : UMR.border}`, background: t === target ? PLAN.accentBg : UMR.white, color: t === target ? PLAN.accentStrong : UMR.black,
              }}
            >
              {t}
            </button>
          ))}
        </div>
        <UMDrawToolbar
          tools={target === 'Bâtiments' ? UM_GEOMETRY_TOOLS.flat().map((x) => (x.id === 'edit' ? { ...x, disabled: !hasBuilding } : x)) : [
            { id: 'draw', icon: 'polyline', label: 'Tracer' },
            { id: 'edit', icon: 'rebase_edit', label: 'Modifier' },
            { id: 'cut', icon: 'content_cut', label: 'Découper' },
            { id: 'delete', icon: 'delete', label: 'Supprimer' },
          ]}
          activeId={tool}
          onSelect={(id) => setTool((c) => (c === id ? null : id))}
        />
        <div style={{ fontSize: 12, color: UMR.textSecondary }}>
          {target === 'Bâtiments' ? 'Astuce : cliquez un bâtiment puis « Éditer » pour corriger ses données.' : `Tracez sur la carte pour modifier la ${target.toLowerCase()} du projet (maquette).`}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, borderBottom: `1px solid ${UMR.borderSubtle}`, paddingBottom: 4 }}>
          Journal des modifications ({entries.length})
        </div>
        {entries.length === 0 && <div style={{ fontSize: 13, color: UMR.textSecondary }}>Aucune modification. Le projet est identique à la copie UrbanMap.</div>}
        {entries.map(([fid, heat]) => (
          <div key={fid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <UMIcon name="edit" size={16} color={PLAN.map.modified} />
            <button type="button" onClick={() => onSelect(fid)} style={{ border: 'none', background: 'transparent', padding: 0, color: UMR.blueLink, cursor: 'pointer', fontFamily: 'monospace' }}>{fid}</button>
            <span style={{ flex: 1, color: UMR.textSecondary }}>{fmt(original[fid])} → <strong style={{ color: UMR.black }}>{fmt(heat)}</strong> MWh</span>
            <button type="button" title="Revenir à la valeur UrbanMap" onClick={() => onRevert(fid)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
              <UMIcon name="undo" size={18} color={UMR.textSecondary} />
            </button>
          </div>
        ))}
      </UMStack>
    </UMSidePanel>
  );
}

// ─── Building provenance (wraps UMBuildingDetailPanel) ─────────────────────

export function ProvenanceBanner({ project, modifiedHeat, originalHeat, drift, editing, onSave, onCancelEdit, onRevert, onOpenUM }: {
  project: PlanProject; fid: string; modifiedHeat?: number; originalHeat: number; drift?: string; editing: boolean;
  onSave: (heat: number) => void; onCancelEdit: () => void; onRevert: () => void; onOpenUM: () => void;
}) {
  const [value, setValue] = useState(String(modifiedHeat ?? originalHeat));
  return (
    <div style={{ background: PLAN.accentBg, borderBottom: `1px solid ${PLAN.accentBorder}`, padding: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontFamily: UMR.font }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: PLAN.accentStrong, fontWeight: 600 }}>
        <UMIcon name="content_copy" size={16} color={PLAN.accentStrong} />
        Copié d’UrbanMap le {project.snapshotDate} · ID conservé
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {modifiedHeat !== undefined ? <Pill tone="warning" icon="edit">Modifié dans le projet</Pill> : <Pill tone="neutral">Identique à la copie</Pill>}
        {drift && <Pill tone="warning" icon="update" title={drift}>A évolué dans UM</Pill>}
      </div>
      {drift && <div style={{ color: PLAN.warningText }}>UrbanMap : {drift} (non reporté)</div>}
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: UMR.white, padding: 8, border: `1px solid ${UMR.border}` }}>
          <label style={{ fontSize: 12 }}>
            Besoin de chaleur total (MWh/an) — valeur UM : {fmt(originalHeat)}
            <input
              type="number"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', height: 30, marginTop: 4, border: `1px solid ${UMR.borderInput}`, padding: '0 8px' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <UMButton variant="secondary" onClick={onCancelEdit}>Annuler</UMButton>
            <UMButton onClick={() => onSave(Number(value) || 0)}>Enregistrer</UMButton>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12 }}>
          {modifiedHeat !== undefined && (
            <button type="button" onClick={onRevert} style={{ border: 'none', background: 'transparent', padding: 0, color: UMR.blueLink, cursor: 'pointer', fontSize: 12 }}>
              Revenir à la valeur copiée
            </button>
          )}
          <button type="button" onClick={onOpenUM} style={{ border: 'none', background: 'transparent', padding: 0, color: UMR.blueLink, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 2 }}>
            Voir dans UrbanMap <UMIcon name="open_in_new" size={13} color={UMR.blueLink} />
          </button>
        </div>
      )}
    </div>
  );
}

